/**
 * @file `/account/inactive` — landing for signed-in users whose
 *       `status === 'inactive'`. Covers both newly-signed-up learners
 *       waiting on review and previously-active learners an operator
 *       has soft-disabled.
 * @module @labcraft/lab/ui/pages/account/inactive
 *
 * User-facing copy stays neutral on which sub-case applies — the
 * learner experience is the same ("your account isn't active right
 * now"), and the platform has no need to disclose whether the cause
 * was waitlist review or an operator action.
 *
 * While the tab is open, polls `/account/me` every 10 seconds via
 * `useMe().refetch()` and navigates onward the moment `status` flips
 * to `active` — so a user who happens to be looking when an operator
 * activates them doesn't have to refresh.
 *
 * Sign-out is a deliberate option here: a user can drop a wrong
 * email and re-sign-up with a corrected address without involving
 * the operator.
 */

import { useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { resolveLandingForLearner } from '../../lib/landing.ts'
import { useMe } from '../../hooks/use_me.ts'
import { useSignoutMutation } from '../../hooks/use_signout.ts'
import styles from '../auth/auth.module.css'

/** Poll interval — `/account/me` is cheap on the server side. */
const POLL_MS = 10_000

export function AccountInactivePage(): JSX.Element {
  const me = useMe()
  const navigate = useNavigate()
  const signout = useSignoutMutation()
  const learner = me.data?.learner ?? null

  useEffect(() => {
    // If the user lands here already active (race between callback
    // redirect and the AuthGuard mounting), bounce them onward
    // immediately instead of waiting for the first poll.
    if (learner && learner.status === 'active') {
      navigate(resolveLandingForLearner(learner, '/'), { replace: true })
      return
    }

    // Drive the refetch on a timer — TanStack also has
    // `refetchInterval` but doing it from an effect makes the
    // cancellation semantics on unmount explicit.
    const handle = window.setInterval(() => {
      void me.refetch()
    }, POLL_MS)
    return () => {
      window.clearInterval(handle)
    }
  }, [learner, navigate, me])

  // Status transitions after refetch land here. `banned` is
  // force-signout territory — handled by the 401-cache-flip in
  // Lab's constructor, not here.
  useEffect(() => {
    if (!learner) {
      // Lost the session mid-poll — bounce.
      if (!me.isPending) navigate('/authorize', { replace: true })
      return
    }
    if (learner.status === 'active') {
      navigate(resolveLandingForLearner(learner, '/'), { replace: true })
    }
  }, [learner, me.isPending, navigate])

  async function onSignOut(): Promise<void> {
    try {
      await signout.mutateAsync()
    } finally {
      navigate('/', { replace: true })
    }
  }

  const email = learner?.email ?? null

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Seat held</p>
        <h1 className={styles.title}>Your seat is being held.</h1>
        <p className={styles.lede}>
          You&apos;re in the queue, and we&apos;ll email{' '}
          {email ? <strong>{email}</strong> : 'you'} the moment your seat opens
          up. Close this tab and come back whenever — or leave it open and
          we&apos;ll wave you through the instant it&apos;s ready.
        </p>

        <p className={styles.fineprint}>
          Used the wrong email?{' '}
          <button
            className={styles.linkButton}
            type="button"
            onClick={onSignOut}
            disabled={signout.isPending}>
            Sign out and try another
          </button>
          .
        </p>
      </section>
    </main>
  )
}
